import { useEffect, useState } from "react";
import { Building2, CheckCircle2, ArrowUpRight, Phone, Globe, MapPin, ShieldCheck, Sparkles, Users } from "lucide-react";
import { api, Logo, errorText } from "./shared.jsx";

const CITY_SUGGESTIONS = ["Haldwani", "Pithoragarh", "Nainital", "Almora", "Ranikhet", "Bhimtal", "Mukteshwar", "Ramnagar", "Kausani", "Bageshwar", "Champawat"];

const BENEFITS = [
  { icon: Sparkles, title: "Free to list", body: "Zero setup cost. DAZYU shows your business to serious homeowners across Kumaon." },
  { icon: Phone, title: "Direct customer contact", body: "Get quotation requests directly. Talk on phone, WhatsApp or in person — no middleman." },
  { icon: ShieldCheck, title: "Verified badge", body: "Our team verifies your business on-site or via documents. Verified badges win 3× more requests." },
  { icon: Users, title: "Own your leads", body: "You keep every relationship. DAZYU never takes commission or acts as a payment intermediary." },
];

export default function ListYourBusiness() {
  const [cats, setCats] = useState([]);
  const [step, setStep] = useState("form"); // form | success | duplicate
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    owner_name: "", email: "", password: "",
    business_name: "", category: "Contractors", subcategory: "",
    city: "Haldwani", locality: "",
    phone: "", website: "",
    description: "",
    services_offered_raw: "",
    years_experience: "", starting_price: "",
  });
  const set = (k, v) => setForm({ ...form, [k]: v });
  useEffect(() => { api.get("/vendor-categories").then(r => setCats(r.data.categories || [])).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault(); if (busy) return;
    setBusy(true);
    try {
      const body = {
        owner_name: form.owner_name.trim(),
        email: form.email.trim(), password: form.password,
        business_name: form.business_name.trim(),
        category: form.category, subcategory: form.subcategory.trim(),
        city: form.city.trim(), locality: form.locality.trim(),
        phone: form.phone.trim(), website: form.website.trim(),
        description: form.description.trim(),
        services_offered: form.services_offered_raw.split(",").map(s => s.trim()).filter(Boolean),
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        starting_price: form.starting_price ? Number(form.starting_price) : null,
      };
      const res = await api.post("/vendors/self-register", body);
      setResult(res.data);
      localStorage.setItem("kumaon_token", res.data.token);
      setStep(res.data.duplicate_of ? "duplicate" : "success");
    } catch (e) { alert(errorText(e)); } finally { setBusy(false); }
  };

  const goDashboard = () => { window.location.href = "/"; };

  if (step === "success" || step === "duplicate") return <div className="lyb-shell">
    <header className="lyb-topbar"><a href="/" data-testid="lyb-home"><Logo/></a></header>
    <div className="lyb-success" data-testid="lyb-success">
      <div className="lyb-success-icon"><CheckCircle2 size={40}/></div>
      <h1>Your business is now on DAZYU as a draft.</h1>
      <p>{step === "duplicate"
        ? "We spotted an existing listing that may be your business — a DAZYU admin will review and merge if it's the same shop."
        : "A DAZYU admin will review your details and verify your business. Once approved, your listing goes public and can start receiving quotation requests."}</p>
      <div className="lyb-success-steps">
        <div><b>1</b>Admin reviews your submission within 48h</div>
        <div><b>2</b>You get an email when your listing is approved</div>
        <div><b>3</b>Customers can then send you quotation requests directly</div>
      </div>
      <button className="button primary" onClick={goDashboard} data-testid="lyb-go-dashboard">Go to my DAZYU dashboard <ArrowUpRight size={16}/></button>
      <small className="muted">Signed in as {result?.user?.email}. You can also log in later from the DAZYU homepage.</small>
    </div>
  </div>;

  return <div className="lyb-shell">
    <header className="lyb-topbar"><a href="/" data-testid="lyb-home"><Logo/></a><a href="/" className="lyb-back">← Back to DAZYU</a></header>
    <section className="lyb-hero">
      <div className="lyb-hero-copy">
        <span className="eyebrow gold">FOR VENDORS · KUMAON</span>
        <h1>List your business.<br/>Meet real customers.</h1>
        <p>DAZYU is the everything-under-one-roof marketplace for homeowners in Kumaon. Architects, contractors, hardware stores, cement dealers, interior designers, modular kitchens — if you build homes, this is where your customers are looking.</p>
        <div className="lyb-benefits">{BENEFITS.map(b => <div key={b.title} className="lyb-benefit" data-testid={`lyb-benefit-${b.title.toLowerCase().replaceAll(" ","-")}`}><b.icon size={18}/><b>{b.title}</b><p>{b.body}</p></div>)}</div>
      </div>
      <form className="lyb-form" onSubmit={submit} data-testid="lyb-form">
        <div className="lyb-form-head"><Building2 size={20}/><h2>Add your business</h2><small>Takes 2 minutes · we review within 48h.</small></div>
        <div className="lyb-fieldset">
          <span className="eyebrow">YOU</span>
          <label>Your full name<input required value={form.owner_name} onChange={e => set("owner_name", e.target.value)} data-testid="lyb-owner-name"/></label>
          <label>Email<input type="email" required value={form.email} onChange={e => set("email", e.target.value)} data-testid="lyb-email"/></label>
          <label>Password (min 8 chars)<input type="password" minLength={8} required value={form.password} onChange={e => set("password", e.target.value)} data-testid="lyb-password"/></label>
        </div>
        <div className="lyb-fieldset">
          <span className="eyebrow">YOUR BUSINESS</span>
          <label>Business name<input required value={form.business_name} onChange={e => set("business_name", e.target.value)} data-testid="lyb-business-name"/></label>
          <div className="lyb-grid-2">
            <label>Category<select value={form.category} onChange={e => set("category", e.target.value)} data-testid="lyb-category">{cats.map(c => <option key={c}>{c}</option>)}</select></label>
            <label>Sub-category<input value={form.subcategory} onChange={e => set("subcategory", e.target.value)} placeholder="e.g. Cement, Kitchen tiles" data-testid="lyb-subcategory"/></label>
          </div>
          <div className="lyb-grid-2">
            <label>City<input list="lyb-cities" required value={form.city} onChange={e => set("city", e.target.value)} data-testid="lyb-city"/>
              <datalist id="lyb-cities">{CITY_SUGGESTIONS.map(c => <option key={c} value={c}/>)}</datalist>
            </label>
            <label>Locality / area<input value={form.locality} onChange={e => set("locality", e.target.value)} placeholder="e.g. Mangal Parao" data-testid="lyb-locality"/></label>
          </div>
          <div className="lyb-grid-2">
            <label>Phone / WhatsApp<input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+91-98XXXXX-XXXXX" data-testid="lyb-phone"/></label>
            <label>Website<input value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://…" data-testid="lyb-website"/></label>
          </div>
          <label>Services you offer (comma-separated)<input value={form.services_offered_raw} onChange={e => set("services_offered_raw", e.target.value)} placeholder="e.g. RCC construction, Interior fit-out, Waterproofing" data-testid="lyb-services"/></label>
          <div className="lyb-grid-2">
            <label>Years of experience<input type="number" min={0} value={form.years_experience} onChange={e => set("years_experience", e.target.value)} data-testid="lyb-experience"/></label>
            <label>Starting price (₹)<input type="number" min={0} value={form.starting_price} onChange={e => set("starting_price", e.target.value)} placeholder="e.g. 350 per sq ft" data-testid="lyb-price"/></label>
          </div>
          <label>About your business<textarea rows={3} value={form.description} onChange={e => set("description", e.target.value)} placeholder="What makes you good at this? Years running, notable projects, what customers love." data-testid="lyb-description"/></label>
        </div>
        <button type="submit" className="button primary full" disabled={busy} data-testid="lyb-submit">{busy ? "Submitting…" : "Submit for review"} <ArrowUpRight size={16}/></button>
        <small className="muted lyb-legal">By submitting, you confirm the information is real and that you own or represent this business. DAZYU verifies every listing before publishing.</small>
      </form>
    </section>
    <footer className="lyb-footer">
      <div><Logo/></div>
      <p><MapPin size={13}/>Serving Kumaon · Haldwani · Pithoragarh · Nainital · Almora and more</p>
      <small className="muted">Everything Under One Roof. © DAZYU {new Date().getFullYear()}</small>
    </footer>
  </div>;
}
